import { envVarUtil, getTelemetrySettings, telemetryBuildTimeEnvVars, urlUtil } from '@cmg/common';

const {
  getOrThrow,
  parseBoolOrThrow,
  parseFloatOrThrow,
  getSessionEnvOverrides,
  getLocalStorageEnvOverrides,
} = envVarUtil;

// Environment variables provided by vite build time variable injection.
// Exported for testing purposes
export const buildTimeEnvVars = {
  ...telemetryBuildTimeEnvVars,
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_CLUSTER_ENVIRONMENT: process.env.REACT_APP_CLUSTER_ENVIRONMENT,
  REACT_APP_ROOT_DOMAIN: process.env.REACT_APP_ROOT_DOMAIN,
  REACT_APP_DATADOG_APPLICATION_ID: process.env.REACT_APP_DATADOG_APPLICATION_ID,
  REACT_APP_DATADOG_CLIENT_TOKEN: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
  REACT_APP_DATADOG_TRACING_ORIGIN: process.env.REACT_APP_DATADOG_TRACING_ORIGIN,
  REACT_APP_MIXPANEL_TOKEN: process.env.REACT_APP_MIXPANEL_TOKEN,
  REACT_APP_AG_GRID_LICENSE_KEY: process.env.REACT_APP_AG_GRID_LICENSE_KEY,
  REACT_APP_MUI_X_PRO_LICENSE_KEY: process.env.REACT_APP_MUI_X_PRO_LICENSE_KEY,
  REACT_APP_OIDC_AUTHORITY_BASE_URL: process.env.REACT_APP_OIDC_AUTHORITY_BASE_URL,
  REACT_APP_AUTH_TOKEN_RENEWAL_INTERVAL_IN_MINUTES:
    process.env.REACT_APP_AUTH_TOKEN_RENEWAL_INTERVAL_IN_MINUTES,
  REACT_APP_IDENTITY_SPA_BASE_URL: process.env.REACT_APP_IDENTITY_SPA_BASE_URL,
  REACT_APP_IDENTITY_SERVICE_BASE_URL: process.env.REACT_APP_IDENTITY_SERVICE_BASE_URL,
  REACT_APP_DL_OFFERING_IDS_PARITY: process.env.REACT_APP_DL_OFFERING_IDS_PARITY,
  REACT_APP_HUBSPOT_SUPPORT_FORM_PORTAL_ID: process.env.REACT_APP_HUBSPOT_SUPPORT_FORM_PORTAL_ID,
  REACT_APP_HUBSPOT_SUPPORT_FORM_ID: process.env.REACT_APP_HUBSPOT_SUPPORT_FORM_ID,
  REACT_APP_SIGNAL_LOG_LEVEL: process.env.REACT_APP_SIGNAL_LOG_LEVEL,
  REACT_APP_ATTESTATION_COPY_LINK_URL: process.env.REACT_APP_ATTESTATION_COPY_LINK_URL,
  REACT_APP__ORDER_BOOK__INSTITUTIONAL_DEMAND_GRID__REFETCH_DEBOUNCE_TIME:
    process.env.REACT_APP__ORDER_BOOK__INSTITUTIONAL_DEMAND_GRID__REFETCH_DEBOUNCE_TIME,
  REACT_APP__ENABLE_FEATURE__ORDER_BOOK__BUY_SIDE__INVESTOR_REPLY:
    process.env.REACT_APP__ENABLE_FEATURE__ORDER_BOOK__BUY_SIDE__INVESTOR_REPLY,
  REACT_APP_BUILD_CLIENT_APP_VERSION: process.env.REACT_APP_BUILD_CLIENT_APP_VERSION,
  REACT_APP__ENABLE_FEATURE__CALENDAR__SUPERSEDING_OFFERINGS:
    process.env.REACT_APP__ENABLE_FEATURE__CALENDAR__SUPERSEDING_OFFERINGS,
  REACT_APP__ENABLE_FEATURE__MULTIPLE_UNDERWRITER_ENTITY_SUPPORT:
    process.env.REACT_APP__ENABLE_FEATURE__MULTIPLE_UNDERWRITER_ENTITY_SUPPORT,
  REACT_APP__ENABLE_FEATURE__CERTIFICATE_REVIEW:
    process.env.REACT_APP__ENABLE_FEATURE__CERTIFICATE_REVIEW,
  REACT_APP_GRAPHQL_SUBSCRIPTIONS_LOGEVENTS: process.env.REACT_APP_GRAPHQL_SUBSCRIPTIONS_LOGEVENTS,
  REACT_APP_GRAPHQL_SUBSCRIPTIONS_REFETCH_BACKOFF:
    process.env.REACT_APP_GRAPHQL_SUBSCRIPTIONS_REFETCH_BACKOFF,
  REACT_APP_GRAPHQL_SUBSCRIPTIONS_FORCE_WEBSOCKET_TENANTS:
    process.env.REACT_APP_GRAPHQL_SUBSCRIPTIONS_FORCE_WEBSOCKET_TENANTS,
  REACT_APP__ENABLE_FEATURE__ORDER_BOOK__INDICATION_ACTIVITY__SUBSCRIPTION:
    process.env.REACT_APP__ENABLE_FEATURE__ORDER_BOOK__INDICATION_ACTIVITY__SUBSCRIPTION,
  REACT_APP__ALLOCATION_BUY_SIDE_DISABLED_BANNER_FOR_AUTHOR_ENTITIES:
    process.env.REACT_APP__ALLOCATION_BUY_SIDE_DISABLED_BANNER_FOR_AUTHOR_ENTITIES,
  REACT_APP__ENABLE_FEATURE__OFFERING_DATA_SEPARATION:
    process.env.REACT_APP__ENABLE_FEATURE__OFFERING_DATA_SEPARATION,
  REACT_APP__ENABLE_FEATURE__ORDER_BOOK__INSTITUTIONAL_DEMAND__LIVE_UPDATES:
    process.env.REACT_APP__ENABLE_FEATURE__ORDER_BOOK__INSTITUTIONAL_DEMAND__LIVE_UPDATES,
  REACT_APP__ENABLE_FEATURE__SETTINGS_MENU: process.env.REACT_APP__ENABLE_FEATURE__SETTINGS_MENU,
  REACT_APP__ENABLE_FEATURE__BUY_SIDE_ECONOMICS:
    process.env.REACT_APP__ENABLE_FEATURE__BUY_SIDE_ECONOMICS,
  REACT_APP__ENABLE_FEATURE__LINKED_ENTITY_ACCESS:
    process.env.REACT_APP__ENABLE_FEATURE__LINKED_ENTITY_ACCESS,
  REACT_APP__ENABLE_FEATURE__FACILITATION_AGENT_ROLE:
    process.env.REACT_APP__ENABLE_FEATURE__FACILITATION_AGENT_ROLE,
};

// Merge runtime variables provided by runtime-env.js which is generated in the docker entrypoint.
// Runtime variables take precedence.
const mergedVars = {
  ...buildTimeEnvVars,
  ...globalThis.env,
  ...getLocalStorageEnvOverrides(),
  ...getSessionEnvOverrides(),
};

const appSettings = {
  env: {
    clusterEnvironment: getOrThrow(mergedVars.REACT_APP_CLUSTER_ENVIRONMENT),
    rootDomain: getOrThrow(mergedVars.REACT_APP_ROOT_DOMAIN),
    secureCookies: !urlUtil.isLocalhost(getOrThrow(mergedVars.REACT_APP_ROOT_DOMAIN)),
    isDevelopment: mergedVars.NODE_ENV === 'development',
    enableDataGridValidations: getOrThrow(mergedVars.REACT_APP_CLUSTER_ENVIRONMENT) === 'local',
    identitySpaBaseUrl: getOrThrow(mergedVars.REACT_APP_IDENTITY_SPA_BASE_URL),
    identityServiceBaseUrl: getOrThrow(mergedVars.REACT_APP_IDENTITY_SERVICE_BASE_URL),
    hubspotSupportFormPortalId: mergedVars.REACT_APP_HUBSPOT_SUPPORT_FORM_PORTAL_ID,
    hubspotSupportFormId: mergedVars.REACT_APP_HUBSPOT_SUPPORT_FORM_ID,
    attestationCopyLinkUrl: mergedVars.REACT_APP_ATTESTATION_COPY_LINK_URL,
    demandGridRefetchDebounce: parseFloatOrThrow(
      mergedVars.REACT_APP__ORDER_BOOK__INSTITUTIONAL_DEMAND_GRID__REFETCH_DEBOUNCE_TIME,
      15_000
    ),
  },
  features: {
    isDLOfferingIdsParity: parseBoolOrThrow(mergedVars.REACT_APP_DL_OFFERING_IDS_PARITY, false),
    isOrderBookBuySideInvestorReplyOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__ORDER_BOOK__BUY_SIDE__INVESTOR_REPLY,
      false
    ),
    isCalendarSupersedingOfferingsOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__CALENDAR__SUPERSEDING_OFFERINGS,
      false
    ),
    isMultipleUnderwriterEntitySupportOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__MULTIPLE_UNDERWRITER_ENTITY_SUPPORT,
      false
    ),
    isCertificateReviewOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__CERTIFICATE_REVIEW,
      false
    ),
    isOrderBookIndicationActivitySubscriptionOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__ORDER_BOOK__INDICATION_ACTIVITY__SUBSCRIPTION,
      false
    ),
    isOdsFunctionalityEnabled: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__OFFERING_DATA_SEPARATION,
      false
    ),
    isOrderBookInstitutionalDemandLiveUpdatesOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__ORDER_BOOK__INSTITUTIONAL_DEMAND__LIVE_UPDATES,
      false
    ),
    isSettingsMenuOn: parseBoolOrThrow(mergedVars.REACT_APP__ENABLE_FEATURE__SETTINGS_MENU, false),
    isBuySideEconomicsOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__BUY_SIDE_ECONOMICS,
      false
    ),
    isLinkedEntityAccessOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__LINKED_ENTITY_ACCESS,
      false
    ),
    isFacilitationAgentRoleOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__FACILITATION_AGENT_ROLE,
      false
    ),
  },
  client: {
    appName: 'cmg-xc-spa',
    appVersion: getOrThrow(mergedVars.REACT_APP_BUILD_CLIENT_APP_VERSION),
    basename: '/',
  },
  logging: {
    mixPanelToken: getOrThrow(mergedVars.REACT_APP_MIXPANEL_TOKEN, 'mock-token'),
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'applicationId'
    datadogApplicationId: mergedVars.REACT_APP_DATADOG_APPLICATION_ID,
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'clientToken'
    datadogClientToken: mergedVars.REACT_APP_DATADOG_CLIENT_TOKEN,
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'allowedTracingOrigins' - single item of array
    datadogTracingOrigin: new RegExp(getOrThrow(mergedVars.REACT_APP_DATADOG_TRACING_ORIGIN)),
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'service'
    datadogServiceName: 'xc-client-web',
  },
  signalR: {
    signalRLogLevel: parseFloatOrThrow(mergedVars.REACT_APP_SIGNAL_LOG_LEVEL, 2),
  },
  licenseKeys: {
    agGridLicenseKey: getOrThrow(mergedVars.REACT_APP_AG_GRID_LICENSE_KEY),
    muiXProLicenseKey: getOrThrow(mergedVars.REACT_APP_MUI_X_PRO_LICENSE_KEY, 'DUMMY_KEY'),
  },
  auth: {
    clientId: 'cmg-app',
    oidcAuthorityBaseUrl: getOrThrow(mergedVars.REACT_APP_OIDC_AUTHORITY_BASE_URL),
    tokenRenewalIntervalInMinutes: parseFloatOrThrow(
      mergedVars.REACT_APP_AUTH_TOKEN_RENEWAL_INTERVAL_IN_MINUTES,
      30
    ),
    fullCalendarLicenseKey: '0449781451-fcs-1603377079',
    scopes: {
      xc: 'xc:full',
      dl: 'dl:full',
      mgmt_api: 'mgmt_api:full',
    },
  },
  graphql: {
    subscriptions: {
      logEvents: parseBoolOrThrow(mergedVars.REACT_APP_GRAPHQL_SUBSCRIPTIONS_LOGEVENTS, false),
      refetch: {
        // The random amount of time that every refetch waits before issuing request to smooth BE load
        backoff: parseFloatOrThrow(
          mergedVars.REACT_APP_GRAPHQL_SUBSCRIPTIONS_REFETCH_BACKOFF,
          3000
        ),
      },
      forceWebsocketTenants: mergedVars.REACT_APP_GRAPHQL_SUBSCRIPTIONS_FORCE_WEBSOCKET_TENANTS
        ? mergedVars.REACT_APP_GRAPHQL_SUBSCRIPTIONS_FORCE_WEBSOCKET_TENANTS.split(',')
        : [],
    },
  },
  telemetry: getTelemetrySettings(mergedVars),
  // An array of sell side tenant cmgEntityKeys. For on platform offerings that these tenants are the settlement agent, the buy side
  // cannot see any allocation information coming from the sell side.
  allocationBuySideDisabledBannerForAuthorEntities:
    mergedVars.REACT_APP__ALLOCATION_BUY_SIDE_DISABLED_BANNER_FOR_AUTHOR_ENTITIES
      ? JSON.parse(mergedVars.REACT_APP__ALLOCATION_BUY_SIDE_DISABLED_BANNER_FOR_AUTHOR_ENTITIES)
      : [],
};

export type AppSettings = typeof appSettings;

export type FeatureToggles = Readonly<AppSettings['features']>;

export type FeatureToggleKeys = keyof FeatureToggles;

export function getAppSettings(): Readonly<AppSettings> {
  return appSettings;
}

export function getFeatureToggles() {
  return getAppSettings().features;
}

export function getEnvVars() {
  return getAppSettings().env;
}

export function getAuthSettings() {
  return getAppSettings().auth;
}

export function getGraphQLSettings() {
  return getAppSettings().graphql;
}
